import React, { useEffect, useState } from "react";
import { useAuthCheck } from "../components/Auth";
import { api } from "../components/Api";
import Loading from "../components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan, faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

export default function PackagingList() {
    const [packagings, setPackagings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);
    const [quantityToAdd, setQuantityToAdd] = useState("");
    const navigate = useNavigate();
    const { checkToken } = useAuthCheck();

    useEffect(() => { checkToken(); }, [checkToken]);

    useEffect(() => {
        const fetchPackagings = async () => {
            setLoading(true);
            try {
                const res = await api.get("/packaging");
                setPackagings(res.data);
            } catch (err) {
                console.error(err);
                alert("Erreur lors de la récupération des packagings.");
            } finally {
                setLoading(false);
            }
        };
        fetchPackagings();
    }, []);

    // Fonction pour diminuer la quantité
    const handleDecreaseQuantity = async (id) => {
        try {
            // Appel API pour diminuer de 1
            const res = await api.patch(`/packaging/${id}/decrease`);
            // Mise à jour locale de la quantité
            setPackagings(packagings.map(p =>
                p.id === id ? { ...p, packaging_quantity: res.data.packaging_quantity } : p
            ));
        } catch (err) {
            console.error(err);
            alert("Impossible de diminuer la quantité.");
        }
    };

    // Ouvrir la modale de réapprovisionnement
    const openReplenishModal = (pack) => {
        setSelectedPack(pack);
        setQuantityToAdd("");
        setShowModal(true);
    };

    // Fermer la modale
    const closeModal = () => {
        setShowModal(false);
        setSelectedPack(null);
        setQuantityToAdd("");
    };

    // Validation du réapprovisionnement
    const handleReplenish = async (e) => {
        e.preventDefault();
        const qty = parseInt(quantityToAdd);
        if (!qty || qty < 1) {
            alert("Veuillez saisir un nombre valide supérieur à 0.");
            return;
        }

        try {
            const res = await api.put(`/packaging/${selectedPack.id}`, { quantity_to_add: qty });

            // Met à jour la liste localement sans recharger toute la page
            setPackagings(packagings.map(p =>
                p.id === selectedPack.id
                    ? { ...p, packaging_quantity: res.data.packaging_quantity }
                    : p
            ));

            closeModal();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la mise à jour de la quantité.");
        }
    };

    // Fonction de suppression
    const handleDelete = async (id) => {
        if (!window.confirm("Voulez-vous vraiment supprimer ce packaging ?")) return;

        try {
            await api.delete(`/packaging/${id}`);
            setPackagings(packagings.filter((p) => p.id !== id));
            alert("Packaging supprimé !");
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la suppression du packaging.");
        }
    };

    const token = localStorage.getItem("token");

    if (!token || localStorage.getItem("role") > 2) {
        navigate("/");
    }

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-2xl font-bold mb-4 dark:text-white">Liste des Packagings</h1>

            <button
                onClick={() => navigate("/add-packaging")}
                className="bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded px-4 py-2 mb-4"
            >
                Ajouter un packaging
            </button>

            {loading ? (
                <Loading message="Chargement des packagings..." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border text-sm text-center">
                        <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                                <th className="border px-2 py-1">Nom</th>
                                <th className="border px-2 py-1">Dimensions</th>
                                <th className="border px-2 py-1">Poids</th>
                                <th className="border px-2 py-1">Quantité</th>
                                <th className="border px-2 py-1">Réaprovisionnement</th>
                                <th className="border px-2 py-1">Lien fournisseur</th>
                                <th className="border px-2 py-1">Supprimer</th>
                            </tr>
                        </thead>
                        <tbody>
                            {packagings.map((pack, index) => {
                                const bgColor = index % 2 === 0 ? "dark:bg-gray-600 bg-gray-050" : "dark:bg-gray-700 bg-gray-100";
                                return (
                                    <tr key={pack.id} className={`${bgColor}`}>
                                        <td className="border px-2 py-1">{pack.packaging_name}</td>
                                        <td className="border px-2 py-1">{pack.packaging_dimension}</td>
                                        <td className="border px-2 py-1">{pack.packaging_weight} g</td>
                                        <td className="border px-2 py-1"><span>{pack.packaging_quantity}</span>
                                            <button
                                                onClick={() => handleDecreaseQuantity(pack.id)}
                                                className="bg-orange-500 hover:bg-orange-400 rounded w-full ml-auto mr-0 mt-1 px-2 py-1"
                                                disabled={pack.packaging_quantity <= 0}
                                            >
                                                −1
                                            </button>
                                        </td>
                                        <td className="border px-2 py-1">
                                            <button
                                                onClick={() => openReplenishModal(pack)}
                                                className="dark:text-blue-300 text-blue-500 text-2xl"
                                            >
                                                <FontAwesomeIcon icon={faSquarePlus} alt="" />
                                            </button>
                                        </td>
                                        <td className="border px-2 py-1">
                                            {pack.packaging_link ? (
                                                <a
                                                    href={pack.packaging_link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="dark:text-blue-300 text-blue-500 hover:underline"
                                                >
                                                    Voir
                                                </a>
                                            ) :
                                                <span>---</span>
                                            }
                                        </td>
                                        <td className="border px-2 py-1">
                                            <button
                                                onClick={() => handleDelete(pack.id)}
                                                className="text-red-500 hover:text-red-700 text-2xl"
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} alt="" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {showModal && selectedPack && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <form
                        onSubmit={handleReplenish}
                        className="dark:bg-gray-700 bg-gray-100 p-6 rounded shadow-lg w-96 space-y-4"
                    >
                        <h2 className="text-xl font-bold text-center">Réapprovisionnement</h2>
                        <p><strong>Nom :</strong> {selectedPack.packaging_name}</p>
                        <p><strong>Quantité actuelle :</strong> {selectedPack.packaging_quantity}</p>

                        <input
                            type="number"
                            min="1"
                            value={quantityToAdd}
                            onChange={(e) => setQuantityToAdd(e.target.value)}
                            className="border p-2 w-full rounded dark:bg-gray-700 bg-white"
                            placeholder="Quantité à ajouter"
                            required
                        />

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeModal}
                                className="px-4 py-2 dark:bg-gray-500 bg-gray-300 rounded"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded"
                            >
                                Ajouter
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
